/**
 * Title Generation Utilities
 * Centralized title and subtitle generation for charts
 * Ensures descriptive, story-telling titles with proper timeframes
 */

export interface TitleContext {
  timeframe: string;
  timeGrouping?: 'weeks' | 'months' | 'quarters';
  isMA?: boolean;
  isTTM?: boolean;
  dataPoints?: number;
  totalValue?: number;
  topCategory?: string;
  topCategoryShare?: number;
  year?: number;
  periodLabel?: string;
  isRealtime?: boolean;
  // New fields for data description generation
  chartType?: 'funding' | 'ma' | 'category' | 'country';
  startWeek?: number;
  endWeek?: number;
  startMonth?: string;
  endMonth?: string;
  // Enhanced fields for AI-driven narrative generation
  topCategories?: Array<{ name: string; value: number; share: number }>;
  secondCategory?: string;
  secondCategoryShare?: number;
  thirdCategory?: string;
  thirdCategoryShare?: number;
  totalCount?: number;
  categoryCount?: number;
  // Temporal context fields for trend analysis
  previousPeriodTotal?: number;
  growthRate?: number; // Percentage change vs previous period
  previousTopCategory?: string;
  previousTopCategoryShare?: number;
  categoryRankChange?: number; // Position change for top category (-1 = dropped, +1 = rose, 0 = stable)
  trend?: 'surge' | 'rising' | 'stable' | 'declining' | 'collapse';
  momentum?: 'accelerating' | 'steady' | 'decelerating';
  isNewCategory?: boolean; // Top category wasn't in previous period's top 3
}

export class TitleUtils {
  /**
   * Calculate temporal trend from growth rate
   */
  static calculateTrend(growthRate: number): 'surge' | 'rising' | 'stable' | 'declining' | 'collapse' {
    if (growthRate >= 50) return 'surge';
    if (growthRate >= 15) return 'rising';
    if (growthRate > -15) return 'stable';
    if (growthRate > -50) return 'declining';
    return 'collapse';
  }

  /**
   * Calculate momentum from current vs previous growth rates
   */
  static calculateMomentum(currentGrowth: number, previousGrowth?: number): 'accelerating' | 'steady' | 'decelerating' {
    if (!previousGrowth) return 'steady';
    const delta = currentGrowth - previousGrowth;
    if (delta > 10) return 'accelerating';
    if (delta < -10) return 'decelerating';
    return 'steady';
  }

  /**
   * Generate descriptive timeframe labels
   */
  static getTimeframeLabel(timeframe: string, timeGrouping: string = 'weeks'): string {
    const timeframeMap: Record<string, string> = {
      '1w': 'Last Week',
      '2w': 'Last 2 Weeks',
      '4w': 'Last 4 Weeks',
      '12w': 'Last 12 Weeks',
      '1q': 'Last Quarter',
      '5q': 'Last 5 Quarters',
      '6m': 'Last 6 Months',
      '9m': 'Last 9 Months',
      'ytd': 'Year to Date',
      'ttm': 'Trailing 12 Months',
      'custom': 'Selected Period'
    };

    return timeframeMap[timeframe] || 'Selected Period';
  }

  /**
   * Generate period description with data points
   */
  static getPeriodDescription(timeframe: string, timeGrouping: string, dataPoints: number): string {
    const timeframeLabel = this.getTimeframeLabel(timeframe, timeGrouping);
    
    if (timeGrouping === 'weeks') {
      return `${timeframeLabel} (${dataPoints} weeks)`;
    } else if (timeGrouping === 'months') {
      return `${timeframeLabel} (${dataPoints} months)`;
    } else if (timeGrouping === 'quarters') {
      return `${timeframeLabel} (${dataPoints} quarters)`;
    }
    
    return `${timeframeLabel} (${dataPoints} periods)`;
  }

  /**
   * Generate funding chart titles
   */
  static generateFundingTitle(context: TitleContext): string {
    const { timeframe, timeGrouping = 'weeks', isTTM } = context;
    
    if (isTTM) {
      return 'Cybersecurity Funding Trends - Trailing 12 Months';
    }
    
    const periodLabel = this.getTimeframeLabel(timeframe, timeGrouping);
    return `Cybersecurity Funding Trends - ${periodLabel}`;
  }

  /**
   * Generate funding chart subtitles
   */
  static generateFundingSubtitle(context: TitleContext): string {
    const { totalValue, dataPoints, timeGrouping = 'weeks' } = context;
    
    if (!totalValue || !dataPoints) {
      return 'Weekly cybersecurity funding activity';
    }

    const avgWeekly = totalValue / dataPoints;
    const avgLabel = avgWeekly >= 1000000000 
      ? `$${(avgWeekly / 1000000000).toFixed(1)}B` 
      : `$${(avgWeekly / 1000000).toFixed(0)}M`;

    return `Average ${avgLabel} per ${timeGrouping.slice(0, -1)} • ${dataPoints} ${timeGrouping}`;
  }

  /**
   * Generate M&A chart titles
   */
  static generateMATitle(context: TitleContext): string {
    const { timeframe, timeGrouping = 'weeks', isTTM } = context;
    
    if (isTTM) {
      return 'Cybersecurity M&A Activity - Trailing 12 Months';
    }
    
    const periodLabel = this.getTimeframeLabel(timeframe, timeGrouping);
    return `Cybersecurity M&A Activity - ${periodLabel}`;
  }

  /**
   * Generate M&A chart subtitles
   */
  static generateMASubtitle(context: TitleContext): string {
    const { totalValue, dataPoints, timeGrouping = 'weeks' } = context;
    
    if (!totalValue || !dataPoints) {
      return 'Weekly cybersecurity acquisition activity';
    }

    const avgWeekly = totalValue / dataPoints;
    const avgLabel = avgWeekly >= 1000000000 
      ? `$${(avgWeekly / 1000000000).toFixed(1)}B` 
      : `$${(avgWeekly / 1000000).toFixed(0)}M`;

    return `Average ${avgLabel} per ${timeGrouping.slice(0, -1)} • ${dataPoints} ${timeGrouping}`;
  }

  /**
   * Generate country funding chart titles
   */
  static generateCountryFundingTitle(context: TitleContext): string {
    const { timeframe, timeGrouping = 'weeks' } = context;
    const periodLabel = this.getTimeframeLabel(timeframe, timeGrouping);
    return `Global Cybersecurity Funding Distribution - ${periodLabel}`;
  }

  /**
   * Generate country funding chart subtitles
   */
  static generateCountryFundingSubtitle(context: TitleContext): string {
    const { topCategory, topCategoryShare, dataPoints, timeGrouping = 'weeks' } = context;
    
    if (!topCategory || !topCategoryShare) {
      return 'Top funding destinations by country';
    }

    return `${topCategory} leads with ${topCategoryShare}% of total funding • ${dataPoints} ${timeGrouping}`;
  }

  /**
   * Generate product category chart titles
   */
  static generateProductCategoryTitle(context: TitleContext): string {
    const { timeframe, timeGrouping = 'weeks' } = context;
    const periodLabel = this.getTimeframeLabel(timeframe, timeGrouping);
    return `Top Product Categories by Funding - ${periodLabel}`;
  }

  /**
   * Generate product category chart subtitles
   */
  static generateProductCategorySubtitle(context: TitleContext): string {
    const { topCategory, topCategoryShare, dataPoints, timeGrouping = 'weeks' } = context;
    
    if (!topCategory || !topCategoryShare) {
      return 'Top product categories by funding volume';
    }

    return `${topCategory} leads with ${topCategoryShare}% of total funding • ${dataPoints} ${timeGrouping}`;
  }

  /**
   * Generate acquisition category chart titles
   */
  static generateAcquisitionCategoryTitle(context: TitleContext): string {
    const { timeframe, timeGrouping = 'weeks' } = context;
    const periodLabel = this.getTimeframeLabel(timeframe, timeGrouping);
    return `M&A Activity by Product Category - ${periodLabel}`;
  }

  /**
   * Generate acquisition category chart subtitles
   */
  static generateAcquisitionCategorySubtitle(context: TitleContext): string {
    const { topCategory, topCategoryShare, dataPoints, timeGrouping = 'weeks' } = context;
    
    if (!topCategory || !topCategoryShare) {
      return 'Top product categories by acquisition activity';
    }

    return `${topCategory} leads with ${topCategoryShare}% of total activity • ${dataPoints} ${timeGrouping}`;
  }

  /**
   * Generate year-over-year chart titles
   */
  static generateYearOverYearTitle(context: TitleContext): string {
    const { isMA, timeframe, timeGrouping = 'weeks' } = context;
    const periodLabel = this.getTimeframeLabel(timeframe, timeGrouping);
    const activityType = isMA ? 'M&A Activity' : 'Funding Trends';
    return `Year-over-Year ${activityType} - ${periodLabel}`;
  }

  /**
   * Generate year-over-year chart subtitles
   */
  static generateYearOverYearSubtitle(context: TitleContext): string {
    const { isMA, dataPoints, timeGrouping = 'weeks' } = context;
    const activityType = isMA ? 'acquisition' : 'funding';
    return `Comparing current and previous year ${activityType} activity • ${dataPoints} ${timeGrouping}`;
  }

  /**
   * Generate growth indicator text
   */
  static generateGrowthIndicator(current: number, previous: number): string {
    if (previous === 0) return 'New data';
    
    const growth = ((current - previous) / previous) * 100;
    const direction = growth >= 0 ? '↑' : '↓';
    const color = growth >= 0 ? '#10B981' : '#EF4444';
    
    return `${direction} ${growth > 0 ? '+' : ''}${growth.toFixed(1)}% YoY`;
  }

  /**
   * Generate market insight text
   */
  static generateMarketInsight(context: TitleContext): string {
    const { totalValue, dataPoints, timeGrouping = 'weeks' } = context;
    
    if (!totalValue || !dataPoints) {
      return 'Market activity data';
    }

    const avgValue = totalValue / dataPoints;
    const avgLabel = avgValue >= 1000000000 
      ? `$${(avgValue / 1000000000).toFixed(1)}B` 
      : `$${(avgValue / 1000000).toFixed(0)}M`;

    return `Average ${avgLabel} per ${timeGrouping.slice(0, -1)}`;
  }

  /**
   * Generate time context for real-time data
   */
  static generateTimeContext(timeframe: string, isRealtime: boolean = false): string {
    const periodLabel = this.getTimeframeLabel(timeframe);
    
    // Always return just the period label without "Live Data" indicator
    return periodLabel;
  }

  /**
   * Generate comprehensive title with context
   */
  static generateComprehensiveTitle(
    baseTitle: string,
    context: TitleContext
  ): { title: string; subtitle: string } {
    const { timeframe, timeGrouping = 'weeks', dataPoints } = context;

    const periodDescription = this.getPeriodDescription(timeframe, timeGrouping, dataPoints || 0);

    return {
      title: `${baseTitle} - ${periodDescription}`,
      subtitle: this.generateMarketInsight(context)
    };
  }

  /**
   * Generate Carta-style data description line
   * Example: "Return on Security M&A activity by product category over the last 12 weeks (Weeks 29-40, 2025)"
   */
  static generateDataDescription(context: TitleContext): string {
    const {
      chartType = 'funding',
      isMA = false,
      timeframe = '12w',
      timeGrouping = 'weeks',
      dataPoints = 12,
      startWeek,
      endWeek,
      startMonth,
      endMonth,
      year = new Date().getFullYear()
    } = context;

    // Activity type - check isMA flag first, then chartType
    const activityType = isMA ? 'M&A activity' : 'funding activity';

    // Chart dimension
    let dimension = '';
    if (chartType === 'category') {
      dimension = ' by product category';
    } else if (chartType === 'country') {
      dimension = ' by country';
    }

    // Time period - use the ACTUAL timeframe, not the aggregation grouping
    // Parse timeframe to get actual period count and unit
    // Examples: "4w" = 4 weeks, "12w" = 12 weeks, "6m" = 6 months, "ytd" = year to date
    let actualPeriod = '';

    // Parse the timeframe string
    if (timeframe === 'ytd') {
      actualPeriod = 'year to date';
    } else if (timeframe === 'ttm') {
      actualPeriod = 'the trailing 12 months';
    } else if (timeframe === 'custom') {
      // For custom ranges, calculate from week range if available
      if (startWeek && endWeek) {
        const weekCount = endWeek - startWeek + 1;
        actualPeriod = weekCount === 1 ? 'the last week' : `the last ${weekCount} weeks`;
      } else {
        actualPeriod = `the last ${dataPoints} ${timeGrouping}`;
      }
    } else if (timeframe === '5q') {
      actualPeriod = 'the last 5 quarters';
    } else if (timeframe === '6m') {
      actualPeriod = 'the last 6 months';
    } else if (timeframe === '9m') {
      actualPeriod = 'the last 9 months';
    } else {
      // Parse standard timeframes like "4w", "12w", "1q"
      const match = timeframe.match(/^(\d+)([wmq])$/);
      if (match) {
        const count = parseInt(match[1]);
        const unit = match[2] === 'w' ? 'week' : match[2] === 'm' ? 'month' : 'quarter';
        actualPeriod = count === 1 ? `the last ${unit}` : `the last ${count} ${unit}s`;
      } else {
        // Fallback to dataPoints + timeGrouping if parsing fails
        actualPeriod = dataPoints === 1
          ? `the last ${timeGrouping.slice(0, -1)}`
          : `the last ${dataPoints} ${timeGrouping}`;
      }
    }

    const periodLabel = actualPeriod;

    // Week/month range if provided
    let rangeLabel = '';
    if (timeGrouping === 'weeks' && startWeek && endWeek) {
      rangeLabel = ` (Weeks ${startWeek}-${endWeek}, ${year})`;
    } else if (timeGrouping === 'months' && startMonth && endMonth) {
      rangeLabel = ` (${startMonth} - ${endMonth} ${year})`;
    }

    return `Return on Security ${activityType}${dimension} over ${periodLabel}${rangeLabel}`;
  }

  /**
   * Generate story-driven title (headline)
   * AI-driven logic that adapts to data patterns, temporal trends, and market structure
   * Example: "Security Operations Surges to Market Leadership"
   */
  static generateStoryTitle(context: TitleContext): string {
    const {
      topCategory,
      secondCategory,
      topCategoryShare,
      secondCategoryShare,
      chartType = 'funding',
      isMA,
      isTTM,
      // Temporal context
      trend,
      momentum,
      categoryRankChange,
      isNewCategory,
      previousTopCategory,
      growthRate
    } = context;

    const activityLabel = isMA ? 'M&A' : 'Funding';

    // TEMPORAL TITLE PATTERNS - Priority when temporal data exists
    if (chartType === 'category' && topCategory) {
      // Temporal Title 1: Explosive surge
      if (trend === 'surge' && growthRate !== undefined && growthRate >= 50) {
        return `${topCategory} Surges to Lead ${activityLabel}`;
      }

      // Temporal Title 2: New category emergence
      if (isNewCategory) {
        return `${topCategory} Emerges to Lead ${activityLabel}`;
      }

      // Temporal Title 3: Leadership change
      if (categoryRankChange && categoryRankChange > 0 && previousTopCategory && topCategory !== previousTopCategory) {
        return `${topCategory} Overtakes ${previousTopCategory}`;
      }

      // Temporal Title 4: Rapid decline / market contraction
      if (trend === 'collapse' && growthRate !== undefined && growthRate < -30) {
        return `${topCategory} Leads ${activityLabel} Amid Decline`;
      }

      // Temporal Title 5: Strong growth with momentum
      if (trend === 'rising' && momentum === 'accelerating') {
        return `${topCategory} Accelerates ${activityLabel}`;
      }

      // Temporal Title 6: Strong growth (stable momentum)
      if (trend === 'rising' && growthRate !== undefined && growthRate >= 15) {
        return `${topCategory} Drives ${activityLabel} Growth`;
      }
    }

    // STRUCTURAL TITLE PATTERNS - Based on market structure when temporal data weak/absent
    if (chartType === 'category' && topCategory) {
      // Structural Title 1: Exact tie
      if (secondCategory && secondCategoryShare && topCategoryShare === secondCategoryShare) {
        // For ties, just use first category to keep title short
        return `${topCategory} Shares ${activityLabel} Lead`;
      }

      // Structural Title 2: Dominant leader (>40%)
      if (topCategoryShare && topCategoryShare > 40) {
        return `${topCategory} Dominates ${activityLabel}`;
      }

      // Structural Title 3: Two co-leaders (within 3%)
      if (secondCategory && secondCategoryShare && Math.abs(topCategoryShare - secondCategoryShare) < 3) {
        // For co-leaders, just use first category to keep title short
        return `${topCategory} Leads ${activityLabel}`;
      }

      // Structural Title 4: Fragmented market
      if (topCategoryShare && topCategoryShare < 25) {
        return `${activityLabel} Market Remains Fragmented`;
      }

      // Structural Title 5: Clear leader with strong challenger
      if (secondCategory && secondCategoryShare && secondCategoryShare >= 15) {
        return `${topCategory} Leads, ${secondCategory} Challenges`;
      }

      // Default structural title
      return `${topCategory} Leads ${activityLabel}`;
    }

    // COUNTRY-SPECIFIC TITLE PATTERNS
    if (chartType === 'country' && topCategory) {
      const activityLabel = isMA ? 'M&A' : 'Funding';

      // Country Title 1: Dominant leader (>50% share)
      if (topCategoryShare && topCategoryShare > 50) {
        return `${topCategory} Dominates ${activityLabel}`;
      }

      // Country Title 2: Clear leader (25-50% share)
      if (topCategoryShare && topCategoryShare >= 25) {
        return `${topCategory} Leads ${activityLabel}`;
      }

      // Country Title 3: Fragmented/distributed market (<25% top share)
      return `${activityLabel} Broadly Distributed Globally`;
    }

    // FUNDING/MA YEAR-OVER-YEAR TITLE PATTERNS
    if ((chartType === 'funding' || chartType === 'ma') && growthRate !== undefined) {
      const activityLabel = isMA ? 'M&A' : 'Funding';
      const timeLabel = isTTM ? 'TTM' : 'Rolling View';

      // YoY Title 1: Explosive surge (>40% growth)
      if (trend === 'surge' || growthRate >= 40) {
        return `${activityLabel} Surges ${growthRate.toFixed(0)}%`;
      }

      // YoY Title 2: Collapse (< -30% decline)
      if (trend === 'collapse' || growthRate <= -30) {
        return `${activityLabel} Drops ${Math.abs(growthRate).toFixed(0)}%`;
      }

      // YoY Title 3: Rising (15-40% growth)
      if (trend === 'rising' || (growthRate >= 15 && growthRate < 40)) {
        return `${activityLabel} Climbs ${growthRate.toFixed(0)}%`;
      }

      // YoY Title 4: Declining (-15% to -30%)
      if (trend === 'declining' || (growthRate < -15 && growthRate > -30)) {
        return `${activityLabel} Declines ${Math.abs(growthRate).toFixed(0)}%`;
      }

      // YoY Title 5: Stable (-15% to +15%)
      if (Math.abs(growthRate) < 15) {
        return `${activityLabel} Trends ${timeLabel.replace(' View', '')}`;
      }
    }

    // Default titles for other chart types
    if (isMA) {
      return 'M&A Activity Trends';
    }

    const defaultTitles: Record<string, string> = {
      'funding': 'Cybersecurity Funding Trends',
      'ma': 'M&A Activity Trends',
      'category': 'Product Category Leaders Emerge',
      'country': 'Global Funding Distribution'
    };

    return defaultTitles[chartType] || 'Cybersecurity Market Activity';
  }

  /**
   * Generate story-driven subtitle (interpretation/narrative)
   * AI-driven logic that adapts to data patterns, timeframes, and market conditions
   * Example: "The industry is betting security operations and compliance automation is the way forward"
   * NOTE: Week ranges should NOT be in subtitle - they belong in the data description line only
   */
  static generateStorySubtitle(context: TitleContext): string {
    const {
      topCategory,
      topCategoryShare,
      secondCategory,
      secondCategoryShare,
      thirdCategory,
      thirdCategoryShare,
      topCategories,
      chartType = 'funding',
      isMA,
      totalCount,
      categoryCount,
      // Temporal context
      growthRate,
      trend,
      momentum,
      categoryRankChange,
      isNewCategory,
      previousTopCategory,
      previousPeriodTotal,
      totalValue
    } = context;

    const activityType = isMA ? 'M&A activity' : 'funding';

    // TEMPORAL PATTERNS - Override with time-based narratives when significant trends exist
    if (chartType === 'category' && topCategory && topCategoryShare) {

      // For M&A charts, use more concise subtitles
      if (isMA) {
        // Temporal Pattern 1: Explosive surge (>50% growth)
        if (trend === 'surge' && growthRate !== undefined && growthRate >= 50) {
          return `${topCategory} leads M&A activity with ${topCategoryShare.toFixed(0)}% share`;
        }

        // Temporal Pattern 2: New category emergence
        if (isNewCategory && topCategory) {
          return `${topCategory} emerges as M&A leader with ${topCategoryShare.toFixed(0)}% share`;
        }

        // Temporal Pattern 3: Leadership change
        if (categoryRankChange && categoryRankChange > 0 && previousTopCategory && topCategory !== previousTopCategory) {
          return `${topCategory} overtakes ${previousTopCategory} as M&A leader`;
        }

        // Temporal Pattern 4: Strong growth (15%+)
        if (trend === 'rising' && growthRate !== undefined && growthRate >= 15) {
          return `${topCategory} leads M&A activity with ${topCategoryShare.toFixed(0)}% share`;
        }

        // Temporal Pattern 5: Declining but still leading
        if (trend === 'declining' && growthRate !== undefined && growthRate < -15) {
          return `${topCategory} maintains M&A leadership with ${topCategoryShare.toFixed(0)}% share`;
        }

        // Default for M&A: Simple leadership statement
        return `${topCategory} leads M&A activity with ${topCategoryShare.toFixed(0)}% share`;
      }

      // For funding charts, use detailed temporal patterns
      // Temporal Pattern 1: Explosive surge (>50% growth)
      if (trend === 'surge' && growthRate !== undefined && growthRate >= 50) {
        return `${topCategory} surged to ${topCategoryShare.toFixed(1)}% share as ${activityType} jumped ${growthRate.toFixed(0)}%`;
      }

      // Temporal Pattern 2: New category emergence
      if (isNewCategory && topCategory) {
        return `${topCategory} emerges as leader with ${topCategoryShare.toFixed(1)}% share, marking a market shift`;
      }

      // Temporal Pattern 3: Leadership change
      if (categoryRankChange && categoryRankChange > 0 && previousTopCategory && topCategory !== previousTopCategory) {
        return `${topCategory} overtakes ${previousTopCategory} to lead with ${topCategoryShare.toFixed(1)}% share`;
      }

      // Temporal Pattern 4: Rapid decline (<-30% growth)
      if (trend === 'collapse' && growthRate !== undefined && growthRate < -30) {
        return `${topCategory} leads at ${topCategoryShare.toFixed(1)}% despite ${activityType} dropping ${Math.abs(growthRate).toFixed(0)}%`;
      }

      // Temporal Pattern 5: Strong growth (15-50%)
      if (trend === 'rising' && growthRate !== undefined && growthRate >= 15) {
        return `${topCategory} leads at ${topCategoryShare.toFixed(1)}% as ${activityType} rose ${growthRate.toFixed(0)}%`;
      }

      // Temporal Pattern 6: Declining but still leading
      if (trend === 'declining' && growthRate !== undefined && growthRate < -15) {
        return `${topCategory} maintains ${topCategoryShare.toFixed(1)}% lead despite ${activityType} declining ${Math.abs(growthRate).toFixed(0)}%`;
      }

      // Temporal Pattern 7: Accelerating momentum (even if moderate growth)
      if (momentum === 'accelerating' && growthRate !== undefined && growthRate > 0) {
        return `${topCategory} leads at ${topCategoryShare.toFixed(1)}% as ${activityType} builds momentum, up ${growthRate.toFixed(0)}%`;
      }

      // If no strong temporal signal, fall through to structural patterns below
    }

    // STRUCTURAL PATTERNS - Static market structure analysis (when temporal data unavailable or weak)
    if (chartType === 'category' && topCategory && topCategoryShare) {
      
      // For M&A charts, use more concise structural patterns
      if (isMA) {
        // Pattern 1: Single dominant leader (>40% share)
        if (topCategoryShare > 40) {
          return `${topCategory} dominates M&A with ${topCategoryShare.toFixed(0)}% share`;
        }

        // Pattern 2: Exact tie between top 2 categories
        if (secondCategory && secondCategoryShare && topCategoryShare === secondCategoryShare) {
          return `${topCategory} and ${secondCategory} tied at ${topCategoryShare.toFixed(0)}% each`;
        }

        // Pattern 3: Two co-leaders (top 2 categories are close, within 3% of each other)
        if (secondCategory && secondCategoryShare && Math.abs(topCategoryShare - secondCategoryShare) < 3) {
          return `${topCategory} and ${secondCategory} lead M&A activity`;
        }

        // Default for M&A: Simple leadership statement
        return `${topCategory} leads M&A activity with ${topCategoryShare.toFixed(0)}% share`;
      }

      // For funding charts, use detailed structural patterns
      // Pattern 1: Single dominant leader (>40% share)
      if (topCategoryShare > 40) {
        return `${topCategory} dominates with ${topCategoryShare.toFixed(1)}% of ${activityType}`;
      }

      // Pattern 2: Exact tie between top 2 categories
      if (secondCategory && secondCategoryShare && topCategoryShare === secondCategoryShare) {
        return `${topCategory} and ${secondCategory} tied at ${topCategoryShare.toFixed(1)}% each, collectively leading ${activityType}`;
      }

      // Pattern 3: Two co-leaders (top 2 categories are close, within 3% of each other)
      if (secondCategory && secondCategoryShare && Math.abs(topCategoryShare - secondCategoryShare) < 3) {
        return `${topCategory} and ${secondCategory} collectively capture ${(topCategoryShare + secondCategoryShare).toFixed(1)}% of ${activityType}`;
      }

      // Pattern 4: Top 3 concentrated (top 3 have >60% combined)
      if (topCategories && topCategories.length >= 3) {
        const top3Share = topCategories.slice(0, 3).reduce((sum, cat) => sum + cat.share, 0);
        if (top3Share > 60) {
          const top3Names = topCategories.slice(0, 3).map(cat => cat.name).join(', ');
          return `${top3Names} capture ${top3Share.toFixed(1)}% of ${activityType}`;
        }
      }

      // Pattern 5: Fragmented market (top category <25%)
      if (topCategoryShare < 25 && categoryCount && categoryCount > 5) {
        return `Fragmented market with ${topCategory} leading at ${topCategoryShare.toFixed(1)}% across ${categoryCount} categories`;
      }

      // Pattern 6: Clear leader with challenger (top has 25-40%, second has 15-30%)
      if (secondCategory && secondCategoryShare && topCategoryShare >= 25 && topCategoryShare <= 40 && secondCategoryShare >= 15) {
        return `${topCategory} leads at ${topCategoryShare.toFixed(1)}%, ${secondCategory} at ${secondCategoryShare.toFixed(1)}%`;
      }

      // Pattern 7: Top 2 strong focus (top 2 have 40-60% combined)
      if (secondCategory && secondCategoryShare) {
        const top2Share = topCategoryShare + secondCategoryShare;
        if (top2Share >= 40 && top2Share < 60) {
          return `${topCategory} and ${secondCategory} lead with ${top2Share.toFixed(1)}% of ${activityType}`;
        }
      }

      // Default pattern: Standard leader narrative
      return `${topCategory} leads with ${topCategoryShare.toFixed(1)}% of ${activityType}`;
    }

    // Country-specific narratives
    if (chartType === 'country' && topCategory && topCategoryShare) {
      const countryCount = categoryCount || 5;

      // Dominant leader (>50%)
      if (topCategoryShare > 50) {
        return `${topCategoryShare.toFixed(1)}% of global ${activityType} across ${countryCount} countries`;
      }

      // Clear leader (25-50%)
      if (topCategoryShare >= 25) {
        return `${topCategoryShare.toFixed(1)}% market share with ${countryCount} active regions`;
      }

      // Fragmented (<25%)
      return `Activity distributed across ${countryCount} countries, ${topCategory} at ${topCategoryShare.toFixed(1)}%`;
    }

    // Funding/MA year-over-year narratives (clean, no arrows)
    if ((chartType === 'funding' || chartType === 'ma') && totalValue !== undefined) {
      const isMAChart = isMA === true;

      if (isMAChart && totalCount !== undefined) {
        // M&A subtitle with deal count
        return `${totalCount} total ${totalCount === 1 ? 'acquisition' : 'acquisitions'}`;
      } else if (totalValue > 0) {
        // Funding subtitle with dollar amount
        const billions = (totalValue / 1000000000).toFixed(2);
        return `$${billions}B total funding raised`;
      }
    }

    // Default narratives by chart type - NO week ranges
    const narratives: Record<string, string> = {
      'funding': 'Market activity reflects investor confidence',
      'ma': 'Strategic consolidation continues',
      'category': 'Market leaders consolidate positions',
      'country': 'Global distribution highlights cyber threats'
    };

    return narratives[chartType] || 'Market activity reflects evolving priorities';
  }
}
